import { BEST_OF_CITY_EVENTS, POPULAR_EVENTS, PRIMARY_BUCKET } from '@/constants';
import { HEAP_LOCATION_STRING } from '@/constants/analytics';
import { ALL_TAB, FOR_YOU, TABS_CONFIG } from '@/constants/tabConfig';
import { useSavedEventIds } from '@/common/useSavedEventIds';
import {
    DESKTOP_DEFAULT_LAYOUT_CONFIG,
    MOBILE_DEFAULT_LAYOUT_CONFIG,
} from '@/containers/BucketContainer/BucketContainer.constants';
import { useAppSelector } from '@/redux/hooks';
import { useAppContext } from '@/common/context';
import type { ClientAuthState } from '@/common/useUserPublicId';
import { selectLocation } from '@/redux/location';
import { BucketConfig } from '@/shared/types';
import { filterUnactionableEvents } from '@/shared/utils/filterUnactionableEvents';
import { formatBucketLabelFromKey } from '@/utils/buckets';
import { getEventsLayout } from '@/utils/feed';
import {
    DestinationEvent,
    transformDestinationEvent,
} from '@eventbrite/consumer-search.event-renderer';
import { useIsMobileDevice } from '@eventbrite/legacy-infra.feature-detection';
import { MessageDescriptorWithValues } from '@/types/intl';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useIntersection } from 'react-use';
import { ConsumerCards } from '../ConsumerCards';
import { EmptyState } from '../EmptyState';
import { LoadingBucket } from '../LoadingBucket';
import { BucketHeader } from './BucketHeader';
import styles from './EventsBucket.module.scss';

const THRESHOLDS = [0, 0.05, 0.1, 0.15];
const SMALL_WIDE_WIDTH_BREAKPOINT = 660;

const useIsInWindow = () => {
    const [isInWindow, setIsInWindow] = useState(false);
    const intersectionRef = useRef<HTMLDivElement>(null);

    // react-use@17's useIntersection types the ref as RefObject<HTMLElement>
    // (non-null), but @types/react@19 makes useRef<T>(null) return
    // RefObject<T | null>. The hook handles a null current at runtime, so bridge
    // the ref type to the hook's expected non-null signature.
    const intersection = useIntersection(intersectionRef as RefObject<HTMLElement>, {
        root: null,
        rootMargin: '0px',
        threshold: THRESHOLDS,
    });

    const isIntersecting =
        intersection?.intersectionRatio && intersection?.intersectionRatio >= 0.05;

    useEffect(() => {
        if (isIntersecting) {
            setIsInWindow(true);
        }
    }, [isIntersecting]);

    return {
        ref: intersectionRef,
        isInWindow,
    };
};

function formatEvents(events: DestinationEvent[] = []) {
    return events.map(transformDestinationEvent);
}

interface EventsBucketProps {
    events: DestinationEvent[];
    loadingState: {
        isFeLoading: boolean;
    };
    subtitle?: MessageDescriptorWithValues;
    icon?: React.ReactNode;
    currentTab?: string;
    authState?: ClientAuthState;
    publicUserId?: string;
    includeNotYetOnSaleEarlyAccess?: boolean;
}

/**
 * This component is a mashup of FeedBuilderContainer and BucketContainer, without redux.
 * Instead, it accepts the list of events as props.
 * It combines the SCSS from both components, and removes most of the logic related to tabs.
 *
 * When FeedBuilderContainer and BucketContainer are migrated, this component can be removed.
 */
export const EventsBucket = ({
    events = [],
    loadingState = { isFeLoading: true },
    subtitle,
    icon,
    currentTab = ALL_TAB,
    authState = 'unknown',
    publicUserId,
    includeNotYetOnSaleEarlyAccess = false,
}: EventsBucketProps) => {
    const { locale } = useAppContext();
    const { currentPlace } = useAppSelector(selectLocation);
    const isMobile = useIsMobileDevice(SMALL_WIDE_WIDTH_BREAKPOINT);
    const { ref } = useIsInWindow();
    const { isFeLoading } = loadingState;
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [fadeClass, setFadeClass] = useState('');

    // Handle smooth transitions when currentTab changes
    useEffect(() => {
        if (!isFeLoading) {
            setIsTransitioning(true);
            setFadeClass('fade-out');

            const timer = setTimeout(() => {
                setFadeClass('fade-in');
                setIsTransitioning(false);
            }, 150);

            return () => clearTimeout(timer);
        }
    }, [currentTab]);

    // Reset fade when loading starts
    useEffect(() => {
        if (isFeLoading) {
            setFadeClass('');
            setIsTransitioning(false);
        }
    }, [isFeLoading]);

    const filteredEvents = useMemo(
        () => filterUnactionableEvents(events, { includeNotYetOnSaleEarlyAccess }),
        [events, includeNotYetOnSaleEarlyAccess],
    );
    const eventIds = useMemo(() => filteredEvents.map((event) => event.id), [filteredEvents]);
    const saveLookupEnabled = authState === 'authenticated';
    const { savedEventIds, isSaveStateReady } = useSavedEventIds(
        publicUserId,
        eventIds,
        saveLookupEnabled,
    );
    const eventsWithSavedState = useMemo(() => {
        if (authState !== 'authenticated' || !isSaveStateReady) {
            return filteredEvents;
        }

        return filteredEvents.map((event) => ({
            ...event,
            saves: {
                ...event.saves,
                saved_by_you: savedEventIds.has(event.id),
            },
        }));
    }, [authState, filteredEvents, isSaveStateReady, savedEventIds]);
    // FIXME Where should this live?
    const formattedEvents = useMemo(
        () => formatEvents(eventsWithSavedState || []),
        [eventsWithSavedState],
    );

    // Hardcode some values for now.
    const bucketKey = PRIMARY_BUCKET;
    const tabKey: string = currentTab;
    const isAuthenticated = authState === 'authenticated';
    const canShowActions = authState !== 'unknown';
    const canShowSaveAction =
        authState === 'anonymous' || (authState === 'authenticated' && isSaveStateReady);

    //TODO: update once hardcoded values are removed
    const currentTabConfig = tabKey === FOR_YOU ? TABS_CONFIG[ALL_TAB] : TABS_CONFIG[currentTab];
    const { name, feedConfig } = currentTabConfig;
    const bucketConfig: BucketConfig = feedConfig[bucketKey];
    const { propsOverwrite } = bucketConfig;

    const bucketClassNames = useMemo(() => {
        const bucketClassNamesDesktop = classNames(
            styles.feedEventsBucket,
            fadeClass && styles[fadeClass],
        );
        const bucketClassNamesMobile = classNames(
            bucketClassNamesDesktop,
            styles.feedEventsBucketMobile,
        );

        return isMobile ? bucketClassNamesMobile : bucketClassNamesDesktop;
    }, [isMobile, bucketKey, fadeClass]);

    const formattedBucketLabel = formatBucketLabelFromKey({
        tabKey,
        name,
        bucketKey,
        currentPlace,
    });

    const layoutConfig = isMobile ? MOBILE_DEFAULT_LAYOUT_CONFIG : DESKTOP_DEFAULT_LAYOUT_CONFIG;
    // Overwrite some classnames because we're using css modules
    layoutConfig.bucketClassNames = styles.feedEventsBucketContent;
    layoutConfig.layout.default.containerClasses = [styles.feedCardCell];

    const eventGroups = useMemo(
        () =>
            getEventsLayout({
                events: formattedEvents,
                bucketConfig: layoutConfig,
            }),
        [layoutConfig, formattedEvents],
    );

    const headerTitle = (
        <BucketHeader
            titleMessage={propsOverwrite?.name ?? name}
            subtitleMessage={subtitle}
            currentPlace={currentPlace}
            isPrimaryBucket={bucketKey === PRIMARY_BUCKET}
            isSpecialBucket={[BEST_OF_CITY_EVENTS, POPULAR_EVENTS].includes(bucketKey || '')}
            icon={icon}
        />
    );

    let content = null;

    if (isFeLoading || isTransitioning) {
        content = (
            <div className={styles.feedEventsBucket} key="loading-component">
                <div
                    data-testid="feed-events-bucket-content"
                    className={`${styles.feedEventsBucketContent} ${styles.feedEventsBucketContentLoading}`}
                >
                    <LoadingBucket />
                </div>
            </div>
        );
    } else {
        //FIXME isInWindow doesn't work yet
        // && !isInWindow) {
        if (isEmpty(events) && tabKey !== FOR_YOU) {
            console.error(`Empty events for tab ${tabKey} and bucket ${bucketKey}`);
            content = (
                <EmptyState
                    bucketKey={bucketKey}
                    emptyState={bucketConfig?.emptyState}
                    isAuthenticated={isAuthenticated}
                />
            );
        } else if (tabKey !== FOR_YOU) {
            content = (
                <ConsumerCards
                    headerTitle={headerTitle}
                    locale={locale}
                    bucketLabel={formattedBucketLabel}
                    isAuthenticated={isAuthenticated}
                    bucketLayoutConfig={layoutConfig}
                    eventGroups={eventGroups}
                    statsigLocationString={HEAP_LOCATION_STRING}
                    affCode={'ehometext'}
                    isForYouTabSelected={tabKey === FOR_YOU}
                    showSaveAction={canShowActions && canShowSaveAction}
                    showShareAction={canShowActions}
                    // getTrackingContext={getTrackingContext}
                />
            );
        }
    }

    return (
        <div
            className={`${styles.root} feed-builder`}
            ref={ref}
            data-testid="events-bucket"
            // data-event-bucket-label={bucketLabel}
        >
            <section className={styles.bucketContainer}>
                <div style={{ minHeight: '100px' }} className={bucketClassNames}>
                    {content}
                </div>
            </section>
        </div>
    );
};
